// NoteSelectionContext.js - Fixed version
import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

const NoteSelectionContext = createContext();

export const NoteSelectionProvider = ({ children }) => {
  // Filters state - matching backend API expectations
  const [filters, setFilters] = useState({
    title: "",
    content: "",
    fromDate: "",
    toDate: ""
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 5,
    hasMore: true,
    totalNotes: 0
  });
  const [isLoading, setIsLoading] = useState(false);

  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Store filtered notes
  const [filteredNotes, setFilteredNotes] = useState([]);
  
  const lastUpdateTimeRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerNoteRefresh = useCallback(() => {
      setRefreshKey(prevKey => prevKey + 1);
    }, []);
  
  
  const setFilteredNotesCallback = useCallback((notes) => {
    const now = Date.now();
    
    // Throttling to prevent excessive updates
    if (now - lastUpdateTimeRef.current < 50) {
      console.log('Throttling setFilteredNotes update');
      return;
    }
    lastUpdateTimeRef.current = now;
    
    console.log('Note Context setFilteredNotes called with:', notes?.length || 0, 'notes');
    
    // Always update with new data
    setFilteredNotes(Array.isArray(notes) ? [...notes] : []);
  }, []);
  
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    // Pagination
    params.set('page', pagination.page.toString());
    params.set('limit', pagination.limit.toString());
    
    // Search term
    if (searchTerm && searchTerm.trim()) {
      params.set('search', searchTerm.trim());
    }
    
    // Apply all filters matching backend expectations
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim()) {
        params.set(key, value.trim());
      }
    });
    
    console.log('Note params:', params.toString());
    return params.toString();
  }, [pagination, searchTerm, filters]);
  
  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      hasMore: true
    }));
  }, []);
  
  // Clear all filters and reset state
  const clearAllFilters = useCallback(() => {
    console.log('Clearing all note filters and resetting state');
    
    setFilters({
      title: "",
      content: "",
      fromDate: "",
      toDate: ""
    });
    
    setSearchTerm('');
    resetPagination();
    
    // Clear the filtered notes
    setFilteredNotes([]);
  }, [resetPagination]);
  
  // Stable setters
  const setFiltersStable = useCallback((newFilters) => {
    setFilters(prevFilters => {
      const hasChanged = Object.keys(newFilters).some(key => 
        newFilters[key] !== prevFilters[key]
      );
      
      if (hasChanged) {
        resetPagination();
        return newFilters;
      }
      
      return prevFilters;
    });
  }, [resetPagination]);
  
  const setSearchTermStable = useCallback((term) => {
    if (term !== searchTerm) {
      setSearchTerm(term);
      resetPagination();
    }
  }, [searchTerm, resetPagination]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchTerm.trim() !== '' ||
           Object.values(filters).some(value => value && value.trim() !== '');
  }, [searchTerm, filters]);
  
  // Update pagination info
  const updatePaginationInfo = useCallback((totalNotes, hasMore) => {
    setPagination(prev => ({
      ...prev,
      totalNotes,
      hasMore
    }));
  }, []);
  
  // Load more notes (for pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  }, [pagination.hasMore]);
  
  const contextValue = useMemo(() => ({
    // Filters
    filters,
    setFilters: setFiltersStable,
    
    // Search
    searchTerm,
    setSearchTerm: setSearchTermStable,
    
    // Pagination
    pagination,
    setPagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    
    // Filtered notes
    filteredNotes,
    setFilteredNotes: setFilteredNotesCallback,

    refreshKey,
    triggerNoteRefresh,
    
    // Utilities
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,


    isLoading, // ADD THIS
    setIsLoading
  }), [
    filters,
    setFiltersStable,
    searchTerm,
    setSearchTermStable,
    pagination,
    resetPagination,
    updatePaginationInfo,
    loadMore,
    filteredNotes,
    setFilteredNotesCallback,
    buildQueryParams,
    clearAllFilters,
    hasActiveFilters,
    refreshKey,
    triggerNoteRefresh
  ]);
  
  return (
    <NoteSelectionContext.Provider value={contextValue}>
      {children}
    </NoteSelectionContext.Provider>
  );
};

export const useNoteSelection = () => {
  const context = useContext(NoteSelectionContext);
  if (!context) {
    throw new Error('useNoteSelection must be used within a NoteSelectionProvider');
  }
  return context;
};